import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, User, Trash2, Edit, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { UserSearchSelect } from './UserSearchSelect';
import { celebrationCelebrantService } from '@/lib/api/celebration-celebrant.service';
import { celebrationInvitationService } from '@/lib/api/celebration-invitation.service';
import { useAuth } from '@/hooks/useAuth';
import type { CelebrationInvitationSummary } from '@/types/celebration-invitation.types';
import { CelebrationCelebrant, UserContact } from '@/types/user-contact.types';

interface CelebrationCelebrantsProps {
  celebrationId?: number; // Made optional for create mode
  onUpdate?: () => void;
  initialCelebrants?: CelebrationCelebrant[]; // For pre-populated data
  onCelebrantsChange?: (celebrants: CelebrationCelebrant[]) => void; // For notifying parent
  containerClassName?: string;
  headerClassName?: string;
  contentClassName?: string;
}

export const CelebrationCelebrants: React.FC<CelebrationCelebrantsProps> = ({
  celebrationId,
  onUpdate,
  initialCelebrants = [],
  onCelebrantsChange,
  containerClassName,
  headerClassName,
  contentClassName,
}) => {
  const { user } = useAuth();
  const { t } = useTranslation(['celebrations']);
  
  const [celebrants, setCelebrants] = useState<CelebrationCelebrant[]>(
    Array.isArray(initialCelebrants) ? initialCelebrants : []
  );
  const [pendingInvitations, setPendingInvitations] = useState<CelebrationInvitationSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [addMode, setAddMode] = useState<'contact' | 'invite'>('contact');
  const [newCelebrantRole, setNewCelebrantRole] = useState<string>('');
  const [selectedContact, setSelectedContact] = useState<UserContact | null>(null);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [respondingInvitationId, setRespondingInvitationId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingRole, setEditingRole] = useState<string>('');
  const [isSurprise, setIsSurprise] = useState<boolean>(false);
  const canManageCelebrants = Boolean(celebrationId);

  const loadCelebrants = useCallback(async () => {
    if (!celebrationId) return;
    
    try {
      setLoading(true);
      const data = await celebrationCelebrantService.getCelebrants(celebrationId);
      // Ensure data is an array before setting
      setCelebrants(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err: any) {
      let errorMessage = 'Failed to load celebrants';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status === 404) {
        errorMessage = 'Celebration not found';
      } else if (err.response?.status === 401) {
        errorMessage = 'Authentication required';
      }
      
      setError(errorMessage);
      console.error('Error loading celebrants:', err);
      // Ensure celebrants remains an array even on error
      setCelebrants([]);
    } finally {
      setLoading(false);
    }
  }, [celebrationId]);

  const loadPendingInvitations = useCallback(async () => {
    if (!celebrationId) return;

    try {
      const invites = await celebrationInvitationService.getCelebrationInvitations(celebrationId, 'celebrant');
      setPendingInvitations(invites);
    } catch (err) {
      console.error('Error loading celebrant invitations:', err);
      setPendingInvitations([]);
    }
  }, [celebrationId]);

  useEffect(() => {
    if (celebrationId) {
      loadCelebrants();
      loadPendingInvitations();
    }
  }, [celebrationId, loadCelebrants, loadPendingInvitations]);

  useEffect(() => {
    if (Array.isArray(celebrants)) {
      onCelebrantsChange?.(celebrants);
    }
  }, [celebrants, onCelebrantsChange]);

  const handleAddCelebrant = async () => {
    if (!celebrationId || !newCelebrantRole.trim()) {
      return;
    }

    if (addMode === 'contact' && !selectedContact) {
      return;
    }

    if (addMode === 'invite' && (!inviteName.trim() || !inviteEmail.trim())) {
      return;
    }

    try {
      setSubmitting(true);

      if (addMode === 'contact') {
        const inviteeEmail = getContactInviteEmail(selectedContact!);

        if (!inviteeEmail) {
          throw new Error('Selected contact needs an email address before you can invite them as a celebrant.');
        }

        await celebrationInvitationService.inviteCelebrant(celebrationId, {
          full_name: getContactDisplayName(selectedContact!),
          email: inviteeEmail,
          role: newCelebrantRole.trim(),
          is_surprise: isSurprise,
        });
      } else {
        await celebrationInvitationService.inviteCelebrant(celebrationId, {
          full_name: inviteName.trim(),
          email: inviteEmail.trim(),
          role: newCelebrantRole.trim(),
          is_surprise: isSurprise,
        });
      }

      await Promise.all([loadCelebrants(), loadPendingInvitations()]);

      setShowAddForm(false);
      setSelectedContact(null);
      setNewCelebrantRole('');
      setIsSurprise(false);
      setInviteName('');
      setInviteEmail('');
      setAddMode('contact');
      onUpdate?.();
    } catch (err: any) {
      console.error('Error adding celebrant:', err);
      let errorMessage = 'Failed to add celebrant';

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err instanceof Error && err.message) {
        errorMessage = err.message;
      } else if (err.response?.status === 400) {
        errorMessage = 'Invalid contact or role information';
      } else if (err.response?.status === 401) {
        errorMessage = 'Authentication required';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error - please try again';
      }

      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateCelebrant = async (id: number) => {
    if (!editingRole.trim()) {
      return;
    }

    try {
      if (celebrationId) {
        // Edit mode: Update in backend
        await celebrationCelebrantService.updateCelebrant(celebrationId, id, {
          role: editingRole.trim()
        });
        await loadCelebrants();
      } else {
        // Create mode: Update in local state
        setCelebrants(prev => 
          prev.map(c => 
            c.id === id 
              ? { ...c, role: editingRole.trim(), updated_at: new Date().toISOString() }
              : c
          )
        );
      }
      
      setEditingId(null);
      setEditingRole('');
      onUpdate?.();
    } catch (err: any) {
      console.error('Error updating celebrant:', err);
      let errorMessage = 'Failed to update celebrant';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      setError(errorMessage);
    }
  };

  const handleDeleteCelebrant = async (id: number) => {
    if (!celebrationId) return;
    if (!window.confirm('Are you sure you want to remove this celebrant?')) {
      return;
    }

    try {
      await celebrationCelebrantService.deleteCelebrant(celebrationId, id);
      await loadCelebrants();
      onUpdate?.();
    } catch (err: any) {
      console.error('Error deleting celebrant:', err);
      let errorMessage = 'Failed to remove celebrant';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      setError(errorMessage);
    }
  };

  const handleAcceptPendingInvitation = async (invitationId: number) => {
    try {
      setRespondingInvitationId(invitationId);
      await celebrationInvitationService.respondToInvitation(invitationId, 'accepted');
      await Promise.all([loadCelebrants(), loadPendingInvitations()]);
      onUpdate?.();
    } catch (err: any) {
      console.error('Error accepting celebrant invitation:', err);
      setError(err.response?.data?.message || 'Failed to accept invitation');
    } finally {
      setRespondingInvitationId(null);
    }
  };

  const startEditing = (celebrant: CelebrationCelebrant) => {
    setEditingId(celebrant.id);
    setEditingRole(celebrant.role);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingRole('');
  };

  const getContactDisplayName = (contact: UserContact) => {
    if (!contact) {
      return 'Unknown Contact';
    }
    
    if (contact.contact_user && contact.contact_user.first_name && contact.contact_user.last_name) {
      return `${contact.contact_user.first_name} ${contact.contact_user.last_name}`;
    }
    
    return contact.full_name || 'Unknown Contact';
  };

  const getContactSubtitle = (contact: UserContact) => {
    if (!contact) {
      return '';
    }
    
    const parts = [];
    if (contact.email) parts.push(contact.email);
    if (contact.relationship) parts.push(contact.relationship);
    return parts.join(' • ');
  };

  const getContactInviteEmail = (contact: UserContact) => {
    return contact.contact_user?.email?.trim() || contact.email?.trim() || '';
  };

  const usedContactIds = [
    ...(Array.isArray(celebrants) ? celebrants.map((celebrant) => celebrant.user_id) : []),
    ...pendingInvitations.map((invitation) => invitation.invitee_user_id),
  ];

  if (loading) {
    return (
      <Card className={cn('rounded-xl border shadow-sm overflow-hidden', containerClassName)}>
        <CardContent className={cn('p-4', contentClassName)}>
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Card className={cn('rounded-xl border shadow-sm overflow-hidden', containerClassName)}>
        <CardHeader className={cn('border-b bg-gradient-to-r from-primary/5 to-transparent px-4 py-3', headerClassName)}>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-sm font-semibold">{t('celebrations:celebrants.title')}</CardTitle>
              <CardDescription>
                {t('celebrations:celebrants.description')}
              </CardDescription>
            </div>
            {canManageCelebrants ? (
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowAddForm(true);
                }}
                className="flex items-center gap-2"
                size="sm"
                type="button"
              >
                <Plus className="h-4 w-4" />
                {t('celebrations:celebrants.addCelebrant')}
              </Button>
            ) : (
              <div className="text-xs text-muted-foreground text-right max-w-[180px]">
                {t('celebrations:celebrants.saveFirst')}
              </div>
            )}
          </div>
        </CardHeader>
        
        <CardContent className={cn('space-y-4 p-4', contentClassName)}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
              {error}
            </div>
          )}

          {/* Add Celebrant Form */}
          {showAddForm ? (
            <div className="border-2 border-dashed border-purple-300 rounded-lg p-4 bg-purple-50">
              <h3 className="font-medium text-gray-900 mb-4">{t('celebrations:celebrants.addNewCelebrant')}</h3>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={addMode === 'contact' ? 'default' : 'outline'}
                    onClick={() => setAddMode('contact')}
                  >
                    {t('celebrations:celebrants.modes.existingContact')}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={addMode === 'invite' ? 'default' : 'outline'}
                    onClick={() => setAddMode('invite')}
                  >
                    {t('celebrations:celebrants.modes.inviteByEmail')}
                  </Button>
                </div>

                {addMode === 'contact' ? (
                  <div>
                    <Label className="block text-sm font-medium text-gray-700 mb-2">{t('celebrations:celebrants.fields.selectContact')}</Label>

                    {!selectedContact ? (
                      <>
                        <UserSearchSelect
                          onSelect={setSelectedContact}
                          placeholder={t('celebrations:celebrants.fields.searchPlaceholder')}
                          excludeIds={usedContactIds}
                          showImportButton={false}
                          onContactImported={() => {
                            if (celebrationId) {
                              loadCelebrants();
                            }
                          }}
                        />

                        <div className="mt-2">
                          <a 
                            href="/user-contacts" 
                            className="text-sm text-purple-600 hover:text-purple-700 inline-flex items-center gap-1"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink className="h-3 w-3" />
                            {t('celebrations:celebrants.fields.addContact')}
                          </a>
                        </div>
                      </>
                    ) : (
                      <div className="p-3 bg-white border-2 border-purple-300 rounded-md">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900">
                              {getContactDisplayName(selectedContact)}
                            </div>
                            {getContactSubtitle(selectedContact) && (
                              <div className="text-sm text-gray-500">
                                {getContactSubtitle(selectedContact)}
                              </div>
                            )}
                            <div className="text-xs text-amber-700 mt-1">
                              {t('celebrations:celebrants.fields.magicLinkHint')}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedContact(null)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            {t('celebrations:celebrants.fields.change')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="invite-name" className="block text-sm font-medium text-gray-700 mb-2">{t('celebrations:celebrants.fields.fullName')}</Label>
                      <Input
                        id="invite-name"
                        value={inviteName}
                        onChange={(event) => setInviteName(event.target.value)}
                        placeholder={t('celebrations:celebrants.fields.fullNamePlaceholder')}
                      />
                    </div>
                    <div>
                      <Label htmlFor="invite-email" className="block text-sm font-medium text-gray-700 mb-2">{t('celebrations:celebrants.fields.email')}</Label>
                      <Input
                        id="invite-email"
                        type="email"
                        value={inviteEmail}
                        onChange={(event) => setInviteEmail(event.target.value)}
                        placeholder={t('celebrations:celebrants.fields.emailPlaceholder')}
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        {t('celebrations:celebrants.fields.emailHint')}
                      </p>
                    </div>
                  </div>
                )}

                <div>
                  <Label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">{t('celebrations:celebrants.fields.role')}</Label>
                  <Input
                    id="role"
                    placeholder={t('celebrations:celebrants.fields.rolePlaceholder')}
                    value={newCelebrantRole}
                    onChange={(e) => setNewCelebrantRole(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="isSurprise"
                    type="checkbox"
                    title={t('celebrations:celebrants.fields.isSurpriseAriaLabel')}
                    checked={isSurprise}
                    onChange={(e) => setIsSurprise(e.target.checked)}
                    className="form-checkbox h-4 w-4 text-purple-600 border-gray-300 rounded"
                  />
                  <Label htmlFor="isSurprise" className="text-sm text-gray-700 select-none cursor-pointer">
                    {t('celebrations:celebrants.fields.isSurprise')}
                  </Label>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleAddCelebrant}
                    disabled={
                      submitting ||
                      !newCelebrantRole.trim() ||
                      (addMode === 'contact' ? !selectedContact : !inviteName.trim() || !inviteEmail.trim())
                    }
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700"
                    type="button"
                  >
                    {submitting ? t('celebrations:celebrants.actions.saving') : t('celebrations:celebrants.actions.sendInvitation')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowAddForm(false);
                      setSelectedContact(null);
                      setNewCelebrantRole('');
                      setIsSurprise(false);
                      setInviteName('');
                      setInviteEmail('');
                      setAddMode('contact');
                    }}
                    size="sm"
                    type="button"
                  >
                    {t('celebrations:celebrants.actions.cancel')}
                  </Button>
                </div>
              </div>
            </div>
          ) : null}

        {/* Celebrants List */}
        {!Array.isArray(celebrants) || celebrants.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <User className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <div className="text-sm">No celebrants added yet</div>
            <div className="text-xs text-gray-400 mt-1 mb-3">
              {canManageCelebrants
                ? 'Add people who are being celebrated in this event'
                : 'Save the celebration first to manage celebrants'}
            </div>
            {canManageCelebrants && (
              <a 
                href="/user-contacts" 
                className="text-sm text-purple-600 hover:text-purple-700 inline-flex items-center gap-1"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="h-3 w-3" />
                Add Contact
              </a>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {celebrants.map((celebrant) => (
              <div
                key={celebrant.id}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-gray-900">
                        {celebrant.user_contact?.full_name || t('celebrations:celebrants.errors.unknownContact')}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      {editingId === celebrant.id ? (
                        <div className="space-y-2 w-full">
                          <div className="flex items-center gap-2">
                            <Input
                              value={editingRole}
                              onChange={(e) => setEditingRole(e.target.value)}
                              placeholder="Role"
                              className="h-6 text-xs"
                              style={{ minWidth: '100px' }}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleUpdateCelebrant(celebrant.id)}
                              disabled={!editingRole.trim()}
                              className="h-6 px-2 text-xs"
                            >
                              {t('celebrations:celebrants.actions.save')}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={cancelEditing}
                              className="h-6 px-2 text-xs"
                            >
                              {t('celebrations:celebrants.actions.cancel')}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <span className="font-medium">{t('celebrations:celebrants.fields.role')}:</span>
                          <span>{celebrant.role}</span>
                        </>
                      )}
                      {celebrant.is_surprise && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300">
                          Surprise
                        </span>
                      )}
                    </div>
                    {(getContactSubtitle(celebrant.user_contact) || celebrant.email) && (
                      <div className="text-xs text-gray-400">
                        {getContactSubtitle(celebrant.user_contact) || celebrant.email}
                      </div>
                    )}
                  </div>
                </div>
                
                {canManageCelebrants && (
                  <div className="flex items-center gap-1">
                    {editingId !== celebrant.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => startEditing(celebrant)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteCelebrant(celebrant.id)}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {pendingInvitations.length > 0 && (
          <div className="space-y-3 pt-3 border-t border-dashed border-amber-200">
            <div className="text-sm font-medium text-slate-700">{t('celebrations:celebrants.pendingInvitations')}</div>
            {pendingInvitations.map((invitation) => (
              <div
                key={`invite-${invitation.id}`}
                className="flex items-center justify-between p-3 border border-amber-200 rounded-lg bg-amber-50/70"
              >
                <div>
                  <div className="font-medium text-slate-900">{invitation.invitee_name}</div>
                  <div className="text-sm text-slate-600">{invitation.invitee_email}</div>
                  <div className="text-sm text-slate-600 mt-1">
                    {t('celebrations:celebrants.fields.role')}: {invitation.celebrant_role}
                    {invitation.is_surprise ? ` · ${t('celebrations:celebrants.fields.isSurprise')}` : ''}
                  </div>
                </div>
                {invitation.invitee_user_id === user?.id ? (
                  <Button
                    size="sm"
                    onClick={() => handleAcceptPendingInvitation(invitation.id)}
                    disabled={respondingInvitationId === invitation.id}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {respondingInvitationId === invitation.id ? t('celebrations:celebrants.actions.accepting') : t('celebrations:celebrants.actions.accept')}
                  </Button>
                ) : (
                  <span className="px-2 py-1 text-xs font-medium rounded-full border bg-amber-100 text-amber-800 border-amber-200">
                    Awaiting response
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
    </div>
  );
};
